import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Sparkles, Target, TrendingUp, FileText, Briefcase, Award, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

export default function LandingPage() {
  const navigate = useNavigate();

  const features = [
    {
      icon: Target,
      title: 'RIASEC Assessment',
      description: 'Discover your personality type with our comprehensive 30-question assessment based on proven career theory.'
    },
    {
      icon: TrendingUp,
      title: 'AI Career Guidance',
      description: 'Get personalized career recommendations powered by advanced AI, tailored to your unique strengths and interests.'
    },
    {
      icon: FileText,
      title: 'Resume Builder',
      description: 'Create professional, ATS-friendly resumes and cover letters that get you noticed by top employers.'
    },
    {
      icon: Briefcase,
      title: 'Job Recommendations',
      description: 'Access curated job opportunities from Freshersworld and Indeed matching your profile and aspirations.'
    }
  ];

  return (
    <div className="min-h-screen">
      <nav className="border-b bg-white/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-xl">
              <Sparkles className="w-6 h-6 text-primary" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-gradient" data-testid="app-logo">Jugnu AI</h1>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/login')}
              className="rounded-full font-semibold"
              data-testid="nav-login-button"
            >
              Sign In
            </Button>
            <Button
              onClick={() => navigate('/register')}
              className="rounded-full font-semibold"
              data-testid="nav-register-button"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      <section className="hero-gradient py-16 md:py-24 lg:py-32 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div 
              className="lg:col-span-6 space-y-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="space-y-4">
                <h1 className="text-5xl md:text-7xl font-heading font-extrabold leading-tight tracking-tighter" data-testid="hero-heading">
                  Your <span className="text-gradient">AI-Powered Career Path</span> and Professional Resume Builder
                </h1>
                <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl" data-testid="hero-description">
                  Launch your career with Jugnu AI — professional AI career counseling and ATS-optimized resume building designed specifically for freshers and graduates.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={() => navigate('/register')}
                  className="rounded-full h-14 px-8 text-base font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                  data-testid="hero-cta-button"
                >
                  Start Your Journey <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="rounded-full h-14 px-8 text-base font-semibold border-2"
                  data-testid="hero-login-button"
                >
                  Sign In
                </Button>
              </div>
              <div className="flex items-center gap-8 pt-4">
                <div className="text-center">
                  <div className="text-3xl font-heading font-bold text-primary">5K+</div>
                  <div className="text-sm text-muted-foreground">Career Paths</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-heading font-bold text-secondary">98%</div>
                  <div className="text-sm text-muted-foreground">Success Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-heading font-bold text-accent">24/7</div>
                  <div className="text-sm text-muted-foreground">AI Support</div>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              className="lg:col-span-6"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 rounded-3xl blur-3xl"></div>
                <img
                  src="https://images.unsplash.com/photo-1742119971773-57e0131095b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHx5b3VuZyUyMGluZGlhbiUyMHByb2Zlc3Npb25hbCUyMHdvcmtpbmclMjBsYXB0b3AlMjBtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzcwNDk2NjA2fDA&ixlib=rb-4.1.0&q=85"
                  alt="AI career counseling dashboard preview showing personalized career paths for freshers"
                  className="relative rounded-3xl shadow-2xl w-full h-auto"
                  data-testid="hero-image"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 px-4 md:px-8 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">
              AI Career Guidance &amp; <span className="text-gradient">ATS Resume Builder</span> for Graduates
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything you need — from graduate career counseling and RIASEC assessments to an ATS-optimized resume builder — to bridge education and employment.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-2 hover:shadow-xl transition-all duration-300 hover:-translate-y-1" data-testid={`feature-card-${index}`}>
                  <CardContent className="p-8 space-y-4">
                    <div className="p-3 bg-primary/10 rounded-2xl w-fit">
                      <feature.icon className="w-8 h-8 text-primary" />
                    </div>
                    <h4 className="text-2xl font-heading font-semibold">{feature.title}</h4>
                    <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 px-4 md:px-8 bg-gradient-to-br from-indigo-50 via-white to-lime-50" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="space-y-4">
            <h2 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">
              Ready to Start Your <span className="text-gradient">Graduate Career Counseling</span> Journey?
            </h2>
            <p className="text-lg text-muted-foreground">
              Join thousands of freshers who found their careers with Jugnu AI&apos;s AI career guidance and ATS resume builder.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/register')}
              className="rounded-full h-14 px-10 text-base font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
              data-testid="cta-register-button"
            >
              Get Started Free <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 px-4 md:px-8 bg-white" data-testid="faq-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 space-y-3">
            <h2 className="text-4xl md:text-5xl font-heading font-bold tracking-tight">
              Frequently Asked <span className="text-gradient">Questions</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              Everything you want to know about AI career counseling and ATS resume building for freshers.
            </p>
          </div>
          <div className="space-y-4">
            {[
              {
                q: 'How does AI Career Counseling help freshers?',
                a: 'Jugnu AI analyzes current market trends and your unique skills to provide personalized career paths, helping graduates bridge the gap between education and employment.',
              },
              {
                q: 'Why is an ATS-friendly resume builder important?',
                a: 'Most companies use Applicant Tracking Systems (ATS) to filter resumes. Jugnu AI ensures your resume is formatted perfectly with the right keywords to pass these filters and reach human recruiters.',
              },
              {
                q: 'Can Jugnu AI help me choose the right career path?',
                a: 'Yes. Our AI career counseling platform evaluates your interests and academic background to suggest the most high-growth industries and roles suited for your profile.',
              },
            ].map((item, idx) => (
              <details
                key={item.q}
                className="group border-2 rounded-2xl bg-white p-5 open:shadow-md transition-all"
                data-testid={`faq-item-${idx}`}
              >
                <summary className="flex justify-between items-center cursor-pointer list-none font-heading font-semibold text-lg text-slate-900">
                  <span>{item.q}</span>
                  <span className="ml-4 text-primary transition-transform group-open:rotate-45 text-2xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-muted-foreground leading-relaxed" data-testid={`faq-answer-${idx}`}>
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>

        {/* JSON-LD structured data — helps Google show FAQ rich snippets on SERPs */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: [
                {
                  '@type': 'Question',
                  name: 'How does AI Career Counseling help freshers?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Jugnu AI analyzes current market trends and your unique skills to provide personalized career paths, helping graduates bridge the gap between education and employment.',
                  },
                },
                {
                  '@type': 'Question',
                  name: 'Why is an ATS-friendly resume builder important?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Most companies use Applicant Tracking Systems (ATS) to filter resumes. Jugnu AI ensures your resume is formatted perfectly with the right keywords to pass these filters and reach human recruiters.',
                  },
                },
                {
                  '@type': 'Question',
                  name: 'Can Jugnu AI help me choose the right career path?',
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Yes. Our AI career counseling platform evaluates your interests and academic background to suggest the most high-growth industries and roles suited for your profile.',
                  },
                },
              ],
            }),
          }}
        />
      </section>

      <footer className="bg-slate-900 text-white py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="p-2 bg-primary/20 rounded-xl">
              <Sparkles className="w-6 h-6 text-primary" />
            </div>
            <h4 className="text-2xl font-heading font-bold">JugnuLive AI</h4>
          </div>
          <p className="text-slate-400">Empowering freshers to find their dream careers</p>
          <p className="text-slate-300 text-sm mt-4" data-testid="footer-support-email">
            Need help? Email us at{' '}
            <a href="mailto:care@jugnu.live" className="text-primary hover:underline font-semibold">
              care@jugnu.live
            </a>
          </p>
          <p className="text-slate-500 text-sm mt-4">&copy; 2026 JugnuLive AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
